import { useEffect, useState, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useIntl } from 'react-intl';
import { Form, InputNumber as AntdInputNumber } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, DatePicker, Select, SelectOption, Checkbox } from 'ui';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { WholesaleDeliveryCondition, WholesaleDeliveryProduct } from 'public/context/CartContext';
import { CartProductForDelivery } from '../types';
import { StyledDetailedDeliveryCard } from '../style';
import dayjs from 'dayjs';

interface AvailableProduct extends CartProductForDelivery {
  availableQuantity: number;
}

interface DeliveryCardProps {
  delivery: WholesaleDeliveryCondition;
  index: number;
  availableProducts: AvailableProduct[];
  onUpdate: (delivery: WholesaleDeliveryCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export interface DeliveryCardRef {
  validateFields: () => Promise<void>;
}

const DeliveryCard = forwardRef<DeliveryCardRef, DeliveryCardProps>(({
  delivery,
  index,
  availableProducts,
  onUpdate,
  onRemove,
  canRemove,
}, ref) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
  }));

  const [showEndDate, setShowEndDate] = useState(!!delivery.delivery_end_date);
  const [allQuantitiesSelected, setAllQuantitiesSelected] = useState(false);
  const isUpdatingRef = useRef(false);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const deliveryRef = useRef(delivery);
  deliveryRef.current = delivery;
  const availableProductsRef = useRef(availableProducts);
  availableProductsRef.current = availableProducts;

  const availableQuantitiesKey = useMemo(() => {
    return availableProducts
      .map((p) => `${p.product_id}:${p.availableQuantity}`)
      .join(',');
  }, [availableProducts]);

  useEffect(() => {
    setShowEndDate(!!delivery.delivery_end_date);
    form.setFieldsValue({
      delivery_address: delivery.delivery_address,
      delivery_start_date: delivery.delivery_start_date
        ? dayjs(delivery.delivery_start_date)
        : undefined,
      delivery_end_date: delivery.delivery_end_date
        ? dayjs(delivery.delivery_end_date)
        : undefined,
      selected_products: delivery.products?.map((p) => p.product_id) || [],
      ...delivery.products?.reduce(
        (acc, p) => ({
          ...acc,
          [`quantity_${p.product_id}`]: p.quantity,
        }),
        {}
      ),
    });
  }, [delivery, form]);

  useEffect(() => {
    if (isUpdatingRef.current) return;

    const currentAvailableProducts = availableProductsRef.current;
    const currentSelected: number[] = form.getFieldValue('selected_products') || [];
    const updatedProducts: WholesaleDeliveryProduct[] = [];
    let hasChanges = false;

    currentSelected.forEach((productId: number) => {
      const product = currentAvailableProducts.find((p) => p.product_id === productId);
      const currentQty = form.getFieldValue(`quantity_${productId}`) || 0;

      if (!product || product.availableQuantity <= 0) {
        hasChanges = true;
        return;
      }

      const clampedQty = Math.min(currentQty, product.availableQuantity);
      if (clampedQty !== currentQty) {
        form.setFieldValue(`quantity_${productId}`, clampedQty);
        hasChanges = true;
      }

      updatedProducts.push({
        product_id: productId,
        farm_id: product.farm_id,
        quantity: clampedQty,
      });
    });

    if (hasChanges) {
      const newSelectedIds = updatedProducts.map((p) => p.product_id);
      form.setFieldValue('selected_products', newSelectedIds);

      isUpdatingRef.current = true;
      onUpdateRef.current({
        ...deliveryRef.current,
        products: updatedProducts,
      });
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [availableQuantitiesKey, delivery.id]);

  const handleValuesChange = (_: unknown, allValues: Record<string, unknown>) => {
    const selectedProductIds = (allValues.selected_products as number[]) || [];

    const products: WholesaleDeliveryProduct[] = selectedProductIds.map((productId) => {
      const product = availableProducts.find((p) => p.product_id === productId);
      const quantity = (allValues[`quantity_${productId}`] as number) || 0;
      if (quantity === 0) {
        form.setFieldValue(`quantity_${productId}`, 1);
      }
      return {
        product_id: productId,
        farm_id: product?.farm_id || 0,
        quantity: quantity || 1,
      };
    });

    const updatedDelivery: WholesaleDeliveryCondition = {
      ...delivery,
      delivery_address: allValues.delivery_address as string,
      delivery_start_date: allValues.delivery_start_date
        ? (allValues.delivery_start_date as dayjs.Dayjs).toISOString()
        : undefined,
      delivery_end_date: allValues.delivery_end_date
        ? (allValues.delivery_end_date as dayjs.Dayjs).toISOString()
        : undefined,
      products,
    };

    onUpdate(updatedDelivery);
  };

  const selectedProducts = Form.useWatch('selected_products', form) || [];

  const allProductsSelected = useMemo(() => {
    const availableIds = availableProducts
      .filter((p) => p.availableQuantity > 0)
      .map((p) => p.product_id);
    return availableIds.length > 0 &&
      availableIds.every((id) => selectedProducts.includes(id));
  }, [availableProducts, selectedProducts]);

  const handleSelectAllProducts = (checked: boolean) => {
    if (checked) {
      const allAvailableProductIds = availableProducts
        .filter((p) => p.availableQuantity > 0)
        .map((p) => p.product_id);
      form.setFieldValue('selected_products', allAvailableProductIds);
    } else {
      form.setFieldValue('selected_products', []);
      setAllQuantitiesSelected(false);
    }

    const allValues = form.getFieldsValue();
    handleValuesChange(null, allValues);
  };

  const handleSelectAllQuantities = (checked: boolean) => {
    setAllQuantitiesSelected(checked);

    if (checked) {
      const currentSelected = form.getFieldValue('selected_products') || [];
      const quantityUpdates: Record<string, number> = {};

      currentSelected.forEach((productId: number) => {
        const product = availableProducts.find((p) => p.product_id === productId);
        if (product) {
          quantityUpdates[`quantity_${productId}`] = product.availableQuantity;
        }
      });

      form.setFieldsValue(quantityUpdates);

      const allValues = form.getFieldsValue();
      handleValuesChange(null, allValues);
    }
  };

  return (
    <StyledDetailedDeliveryCard>
      <div className="card-header">
        <div className="card-title">
          {intl.formatMessage({ id: 'wholesale.delivery.condition' })} #{index + 1}
        </div>
        {canRemove && (
          <div className="card-actions">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={onRemove}
              danger
            />
          </div>
        )}
      </div>

      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <WithoutModalAddressFinder
          form={form}
          name="delivery_address"
          label={intl.formatMessage({ id: 'wholesale.delivery.address' })}
          disabled={false}
          validations="required"
        />

        <div className="date-range-row">
          <DatePicker
            name="delivery_start_date"
            label={intl.formatMessage({ id: 'wholesale.delivery.start_date' })}
          />
          {showEndDate && (
            <DatePicker
              name="delivery_end_date"
              label={intl.formatMessage({ id: 'wholesale.delivery.end_date' })}
            />
          )}
        </div>
        <Checkbox
          checked={showEndDate}
          onChange={(e) => {
            setShowEndDate(e.target.checked);
            if (!e.target.checked) {
              form.setFieldValue('delivery_end_date', undefined);
              onUpdate({
                ...delivery,
                delivery_end_date: undefined,
              });
            }
          }}
          className="set-period-checkbox"
          label={intl.formatMessage({ id: 'wholesale.delivery.set_period' })}
        />

        <Select
          name="selected_products"
          label={intl.formatMessage({ id: 'wholesale.delivery.products' })}
          mode="multiple"
          placeholder={intl.formatMessage({ id: 'wholesale.delivery.select_products' })}
        >
          {availableProducts
            .filter((p) => p.availableQuantity > 0)
            .map((p) => (
              <SelectOption key={p.product_id} value={p.product_id}>
                {`${p.product_name} (${intl.formatMessage(
                  { id: 'wholesale.delivery.available' },
                  { quantity: p.availableQuantity, unit: p.unit || '' }
                )})`}
              </SelectOption>
            ))}
        </Select>

        <div className="checkbox-row">
          <Checkbox
            checked={allProductsSelected}
            onChange={(e) => handleSelectAllProducts(e.target.checked)}
            label={intl.formatMessage({ id: 'wholesale.delivery.all_products' })}
          />
          <Checkbox
            checked={allQuantitiesSelected}
            onChange={(e) => handleSelectAllQuantities(e.target.checked)}
            disabled={selectedProducts.length === 0}
            label={intl.formatMessage({ id: 'wholesale.delivery.all_quantities' })}
          />
        </div>

        {selectedProducts.length > 0 && (
          <div className="product-list">
            {selectedProducts.map((productId: number) => {
              const product = availableProducts.find((p) => p.product_id === productId);
              if (!product) return null;

              return (
                <div key={productId} className="product-item">
                  <div className="product-name">{product.product_name}</div>
                  <div className="product-quantity">
                    <div className="amount-selector">
                      <button
                        type="button"
                        className="btn-amount"
                        onClick={() => {
                          const current = form.getFieldValue(`quantity_${productId}`) || 0;
                          const newVal = Math.max(1, current - 1);
                          form.setFieldValue(`quantity_${productId}`, newVal);
                          handleValuesChange(null, form.getFieldsValue());
                        }}
                      >
                        −
                      </button>
                      <div className="unit-value">
                        <Form.Item name={`quantity_${productId}`} noStyle>
                          <AntdInputNumber
                            min={1}
                            max={product.availableQuantity}
                            step={1}
                            precision={0}
                            controls={false}
                          />
                        </Form.Item>
                        <span className="unit-label">{product.unit}</span>
                      </div>
                      <button
                        type="button"
                        className="btn-amount"
                        onClick={() => {
                          const current = form.getFieldValue(`quantity_${productId}`) || 0;
                          const newVal = Math.min(product.availableQuantity, current + 1);
                          form.setFieldValue(`quantity_${productId}`, newVal);
                          handleValuesChange(null, form.getFieldsValue());
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Form>
    </StyledDetailedDeliveryCard>
  );
});

export default DeliveryCard;
